import React, { useEffect, useState, useRef } from "react";
import { Package, ShoppingBag, Truck, BarChart3, MoveRight } from "lucide-react";
import { motion, useInView } from "framer-motion";

// Counter
const Counter = ({ value, suffix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(value);
      const duration = 2000;
      let startTime = null;

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setDisplayValue(Math.floor(progress * (end - start) + start));
        if (progress < 1) requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    }
  }, [isInView, value]);

  return <span ref={ref}>{displayValue}{suffix}</span>;
};

const BenefitsSection = () => {
  const benefits = [
    {
      icon: <Package className="w-6 h-6" />,
      title: "Bulk Buying at Lower Prices",
      desc: "Unlock wholesale margins that keep your business competitive."
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: "Wide Range of Products",
      desc: "From staples to snacks, access everything in one dashboard."
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Reliable Delivery & Tracking",
      desc: "Precision logistics designed for high-volume retail demands."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Business Growth Support",
      desc: "Data-driven insights to help you scale smarter."
    },
  ];

  return (
    <section className="bg-white py-24 px-6 md:px-16">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-[2px] bg-yellow-500"></span>
              <h4 className="text-yellow-500 font-semibold tracking-[0.3em] uppercase text-xs">
                Partnership Perks
              </h4>
            </div>

            <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
              Scale Your <br />
              <span className="text-yellow-500">Wholesale.</span>
            </h2>
          </motion.div>

          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gray-600 md:w-1/3 text-sm leading-relaxed border-l border-gray-200 pl-6"
          >
            A high-speed wholesale network designed to empower local retailers. 
            No complexity, just seamless logistics.
          </motion.p>
        </div>

        {/* Cards */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {benefits.map((benefit, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#fafafa] border border-gray-200 rounded-xl p-8 flex flex-col group hover:shadow-lg transition"
            >
              <div className="mb-6 p-3 w-fit bg-yellow-100 text-yellow-600 rounded-lg group-hover:scale-105 transition">
                {benefit.icon}
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-3">
                {benefit.title}
              </h3>

              <p className="text-gray-600 text-sm leading-relaxed flex-grow mb-6">
                {benefit.desc}
              </p>

              <div className="flex items-center gap-2 text-gray-400 group-hover:text-yellow-500 transition text-xs font-semibold uppercase tracking-wide">
                System Active 
                <MoveRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Counters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mt-20 pt-10 border-t border-gray-200 flex flex-wrap gap-16 justify-center md:justify-start"
        >
          <div>
            <span className="text-4xl font-extrabold text-gray-900">
              <Counter value="24" suffix="/7" />
            </span>
            <p className="text-xs text-gray-500 uppercase mt-1">Logistic Support</p>
          </div>

          <div>
            <span className="text-4xl font-extrabold text-gray-900">
              <Counter value="10" suffix="k+" />
            </span>
            <p className="text-xs text-gray-500 uppercase mt-1">Product SKUs</p>
          </div>

          <div>
            <span className="text-4xl font-extrabold text-yellow-500">
              <Counter value="100" suffix="%" />
            </span>
            <p className="text-xs text-gray-500 uppercase mt-1">Transparency</p>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default BenefitsSection;